// externals
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { isNil } from 'lodash';

// libraries
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import { SelectInput } from '@interstate/components/SelectInput';
import { TextInput } from '@interstate/components/TextInput';
import { NumericInput } from '@interstate/components/NumericInput';
import { Box } from '@interstate/components/Box';
import { extractNumberFromInputValue } from '@makemydeal/dr-shared-ui-utils';
import type { Rate, RatedProtectionProduct } from '@makemydeal/dr-platform-types';
import { formatNumber } from '@makemydeal/dr-common-utils';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';

// types
import type { EditProductTermsProps } from '../../types';
import type { ValidationEngine } from '@makemydeal/dr-shared-ui-utils/validationEngine/ValidationEngine';

// styles
import { ResponsiveFieldRow } from './EditProductPrices.style';

// components
import EditProductTaxable from './EditProductTaxable';

// utils
import {
    getDeductibleOptions,
    getIntervalOptions,
    getMilesOptions,
    getTermsOptions,
    parseDeductible,
    hasCorrectTerm,
    hasCorrectMiles,
    hasCorrectDeductible,
    hasCorrectServiceInterval,
    hasCorrectPlan,
    findRate,
    getDeductibleValue,
    parseManualDeductible,
    getManualDeductibleInputValue
} from '../../utils/termsUtils';

const TERM_FIELD_MAX_DIGITS = 9;
const TEXT_FIELD_MAX_LENGTH = 255;

const getTermErrorMessage = (
    hasContractFlowError: boolean,
    contractErrorMessage: string | undefined,
    hasApplicableTerm: boolean
) => {
    if (hasContractFlowError) return contractErrorMessage;
    if (!hasApplicableTerm) return 'The term is invalid, please replace it with a valid value.';
    return '';
};

const EditProductTerms = ({
    productProps,
    productRates,
    rateAttributes,
    onEdit,
    validator,
    formErrors,
    onValidationError,
    isProductTaxable,
    isManualMode = false
}: {
    productProps: EditProductTermsProps;
    productRates: Rate[];
    rateAttributes: RatedProtectionProduct['rateAttributes'];
    onEdit: (productProps: EditProductTermsProps) => void;
    validator: ValidationEngine;
    formErrors: any;
    onValidationError: (hasError: boolean) => void;
    isProductTaxable?: boolean;
    showOnlyTermMonths?: boolean;
    isManualMode?: boolean;
}) => {
    const findRateWithPlan = useCallback(
        (attributes: Partial<Rate>) => {
            return findRate(productRates, {
                ...attributes,
                productProviderPlan: productProps.productProviderPlan
            });
        },
        [productRates, productProps]
    );

    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const dealTerm = useSelector(offerSelectors.getSelectedTermMonths);
    const handleMonthsChange = useCallback(
        (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
            const newMonths = extractNumberFromInputValue(event.target.value);
            const selectedRate = findRateWithPlan({
                productCoverageLength: newMonths
            }) as Rate;
            onEdit({
                productProviderPlan: selectedRate.productProviderPlan,
                productCoverageLength: newMonths,
                productCoverageMiles: selectedRate.productCoverageMiles,
                productDeductible: selectedRate.productDeductible,
                productDeductibleMethod: selectedRate.deductibleApplyType,
                productServiceInterval: selectedRate.productServiceInterval
            });

            if (isContractingContext && dealTerm) {
                // Ensure context has the latest deal term before validating
                validator.setValue('terms', dealTerm);
                validator.validatePath('terms', newMonths || 0);
            }
        },
        [onEdit, findRateWithPlan, isContractingContext, dealTerm]
    );

    const handleMilesChange = useCallback(
        (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
            const newMiles = extractNumberFromInputValue(event.target.value);
            const selectedRate = findRateWithPlan({
                productCoverageLength: productProps.productCoverageLength,
                productCoverageMiles: newMiles
            }) as Rate;
            onEdit({
                productProviderPlan: selectedRate.productProviderPlan,
                productCoverageMiles: newMiles,
                productDeductible: selectedRate.productDeductible,
                productDeductibleMethod: selectedRate.deductibleApplyType,
                productServiceInterval: selectedRate.productServiceInterval
            });
        },
        [onEdit, productProps, findRateWithPlan]
    );

    const handleDeductibleChange = useCallback(
        (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
            const newDeductible = parseDeductible(String(event.target.value));
            const { deductibleValue: productDeductible, deductibleApplyType: productDeductibleMethod } = newDeductible;
            const selectedRate = findRateWithPlan({
                productCoverageLength: productProps.productCoverageLength,
                productCoverageMiles: productProps.productCoverageMiles,
                productDeductible,
                deductibleApplyType: productDeductibleMethod
            }) as Rate;
            onEdit({
                productProviderPlan: selectedRate.productProviderPlan,
                productDeductible,
                productDeductibleMethod,
                productServiceInterval: selectedRate.productServiceInterval
            });
        },
        [onEdit, productProps, findRateWithPlan]
    );

    const handleServiceIntervalChange = useCallback(
        (event: InterstateOnChangeEvent<SelectInputEventValue>) => {
            const newInterval = event.target.value;
            onEdit({
                productServiceInterval: newInterval as string
            });
        },
        [onEdit]
    );

    // Manual override mode: free-text inputs that bypass rate matching. The deductible keeps a local raw string so
    // in-progress edits (e.g. a trailing space before the method) survive the value <-> method round trip.
    const canonicalDeductibleInput = getManualDeductibleInputValue(
        productProps.productDeductible,
        productProps.productDeductibleMethod
    );
    const [deductibleInput, setDeductibleInput] = useState(canonicalDeductibleInput);
    const prevCanonicalDeductibleRef = useRef(canonicalDeductibleInput);

    useEffect(() => {
        // Re-seed the raw input only when the canonical value changed from outside this component (e.g. product swap).
        if (canonicalDeductibleInput !== prevCanonicalDeductibleRef.current) {
            prevCanonicalDeductibleRef.current = canonicalDeductibleInput;
            setDeductibleInput(canonicalDeductibleInput);
        }
    }, [canonicalDeductibleInput]);

    const handleManualMonthsChange = useCallback(
        (event: InterstateOnChangeEvent<string>) => {
            const raw = String(event.target.value);
            onEdit({ productCoverageLength: raw === '' ? undefined : Number(raw) });
        },
        [onEdit]
    );

    const handleManualMilesChange = useCallback(
        (event: InterstateOnChangeEvent<string>) => {
            const raw = String(event.target.value);
            onEdit({ productCoverageMiles: raw === '' ? undefined : Number(raw) });
        },
        [onEdit]
    );

    const handleManualIntervalChange = useCallback(
        (event: InterstateOnChangeEvent<string>) => {
            const raw = event.target.value;
            onEdit({ productServiceInterval: raw === '' ? undefined : (raw as string) });
        },
        [onEdit]
    );

    const handleManualDeductibleChange = useCallback(
        (event: InterstateOnChangeEvent<string>) => {
            const raw = event.target.value;
            setDeductibleInput(raw);
            const { productDeductible, productDeductibleMethod } = parseManualDeductible(raw);
            prevCanonicalDeductibleRef.current = getManualDeductibleInputValue(productDeductible, productDeductibleMethod);
            onEdit({ productDeductible, productDeductibleMethod });
        },
        [onEdit]
    );

    const hasApplicablePlan = useMemo(
        () => hasCorrectPlan(productRates, rateAttributes, productProps.productProviderPlan),
        [productRates, rateAttributes, productProps.productProviderPlan]
    );

    const hasApplicableTerm = useMemo(
        () => hasCorrectTerm(productRates, rateAttributes, productProps.productCoverageLength),
        [productRates, rateAttributes, productProps.productCoverageLength]
    );

    const hasApplicableMiles = useMemo(
        () => hasCorrectMiles(productRates, rateAttributes, productProps.productCoverageMiles),
        [productRates, rateAttributes, productProps.productCoverageMiles]
    );

    const hasApplicableDeductible = useMemo(
        () =>
            hasCorrectDeductible(
                productRates,
                rateAttributes,
                productProps.productDeductible,
                productProps.productDeductibleMethod
            ),
        [productRates, rateAttributes, productProps.productDeductible, productProps.productDeductibleMethod]
    );

    const hasApplicableServiceInterval = useMemo(
        () => hasCorrectServiceInterval(productRates, rateAttributes, productProps.productServiceInterval),
        [productRates, rateAttributes, productProps.productServiceInterval]
    );

    const termOptions = useMemo(
        () => getTermsOptions(productRates, productProps.productProviderPlan),
        [productProps.productProviderPlan, productRates]
    );
    const milesOptions = useMemo(() => getMilesOptions(productRates, productProps), [productProps, productRates]);
    const deductiblesOptions = useMemo(() => getDeductibleOptions(productRates, productProps), [productProps, productRates]);
    const intervalOptions = useMemo(() => getIntervalOptions(productRates, productProps), [productProps, productRates]);

    const hasProductIntervalError =
        hasApplicablePlan && hasApplicableTerm && hasApplicableMiles && hasApplicableDeductible && !hasApplicableServiceInterval;
    const hasProductTermMonthsError = hasApplicablePlan && !hasApplicableTerm;
    const hasProductTermMilesError = hasApplicableTerm && !hasApplicableMiles;
    const hasProductDeductibleError = hasApplicablePlan && hasApplicableTerm && hasApplicableMiles && !hasApplicableDeductible;
    const hasContractFlowError = isContractingContext && formErrors.terms?.hasError;

    const hasError =
        hasProductDeductibleError ||
        hasProductIntervalError ||
        hasProductTermMonthsError ||
        hasProductTermMilesError ||
        hasContractFlowError;

    // Manual override values skip rate-match validation (they may not correspond to any rate option), but the
    // API-flagged coverage-term errors still apply in both rated and manual modes.
    const effectiveHasError =
        (isManualMode ? false : hasError) || Boolean(formErrors.apiTerms?.hasError) || Boolean(formErrors.apiMiles?.hasError);

    useEffect(() => {
        if (isContractingContext && dealTerm) {
            validator.setValue('terms', dealTerm);
            validator.validatePath('terms', productProps.productCoverageLength || 0);
        } else if (!isContractingContext) {
            validator.clearErrors();
        }
    }, [isContractingContext, productProps.productCoverageLength, dealTerm, validator]);

    useEffect(() => {
        onValidationError(effectiveHasError);
    }, [effectiveHasError, onValidationError]);

    if (isManualMode) {
        return (
            <>
                <ResponsiveFieldRow>
                    <EditProductTaxable
                        productProps={{
                            isProductTaxable: isProductTaxable
                        }}
                    />
                    <Box flex={1}>
                        <NumericInput
                            data-testid="edit-product-term-months-input"
                            label="Term Months"
                            name="productCoverageLength"
                            maxLength={TERM_FIELD_MAX_DIGITS}
                            value={!isNil(productProps.productCoverageLength) ? String(productProps.productCoverageLength) : ''}
                            onChange={handleManualMonthsChange}
                            hasError={formErrors.apiTerms?.hasError}
                            errorMessage={formErrors.apiTerms?.errorMessage}
                        />
                    </Box>
                </ResponsiveFieldRow>
                <Box marginTop="1rem">
                    <ResponsiveFieldRow>
                        <NumericInput
                            data-testid="edit-product-term-miles-input"
                            label="Term Miles"
                            name="productCoverageMiles"
                            maxLength={TERM_FIELD_MAX_DIGITS}
                            value={!isNil(productProps.productCoverageMiles) ? String(productProps.productCoverageMiles) : ''}
                            onChange={handleManualMilesChange}
                            hasError={formErrors.apiMiles?.hasError}
                            errorMessage={formErrors.apiMiles?.errorMessage}
                        />
                        <TextInput
                            data-testid="edit-product-deductible-input"
                            label="Deductible"
                            name="productDeductible"
                            maxLength={TEXT_FIELD_MAX_LENGTH}
                            value={deductibleInput}
                            onChange={handleManualDeductibleChange}
                        />
                    </ResponsiveFieldRow>
                </Box>
                <Box marginTop="1rem">
                    <TextInput
                        data-testid="edit-product-interval-input"
                        label="Interval"
                        name="productServiceInterval"
                        maxLength={TEXT_FIELD_MAX_LENGTH}
                        value={productProps.productServiceInterval ?? ''}
                        onChange={handleManualIntervalChange}
                    />
                </Box>
            </>
        );
    }

    return (
        <>
            <ResponsiveFieldRow>
                <EditProductTaxable
                    productProps={{
                        isProductTaxable: isProductTaxable
                    }}
                />
                <Box flex={1}>
                    {termOptions[0] && (
                        <SelectInput
                            data-testid="edit-product-term-months"
                            label="Term Months"
                            name="productCoverageLength"
                            onChange={handleMonthsChange}
                            placeholder={String(productProps.productCoverageLength)}
                            value={
                                !isNil(productProps.productCoverageLength)
                                    ? String(productProps.productCoverageLength)
                                    : termOptions[0].label
                            }
                            hasError={hasProductTermMonthsError || hasContractFlowError}
                            errorMessage={getTermErrorMessage(
                                hasContractFlowError,
                                formErrors.terms?.errorMessage,
                                hasApplicableTerm
                            )}
                            options={termOptions}
                            displayDeselectOption={false}
                        />
                    )}
                </Box>
            </ResponsiveFieldRow>
            <Box marginTop="1rem">
                <ResponsiveFieldRow>
                    {milesOptions[0] && (
                        <SelectInput
                            data-testid="edit-product-term-miles"
                            label="Term Miles"
                            name="productCoverageMiles"
                            onChange={handleMilesChange}
                            placeholder={
                                !isNil(productProps.productCoverageMiles)
                                    ? formatNumber(productProps.productCoverageMiles)
                                    : undefined
                            }
                            value={
                                !isNil(productProps.productCoverageMiles)
                                    ? formatNumber(productProps.productCoverageMiles)
                                    : milesOptions[0].value
                            }
                            options={milesOptions}
                            hasError={hasProductTermMilesError}
                            errorMessage={hasApplicableMiles ? '' : 'The mileage is invalid, please replace it with a valid value.'}
                            displayDeselectOption={false}
                        />
                    )}
                    {deductiblesOptions[0] && (
                        <SelectInput
                            data-testid="edit-product-deductible"
                            label="Deductible"
                            name="productDeductible"
                            onChange={handleDeductibleChange}
                            placeholder="Select"
                            value={
                                !(isNil(productProps.productDeductible) && isNil(productProps.productDeductibleMethod))
                                    ? getDeductibleValue(productProps.productDeductible, productProps.productDeductibleMethod)
                                    : ''
                            }
                            options={deductiblesOptions}
                            hasError={hasProductDeductibleError}
                            errorMessage={
                                hasApplicableDeductible ? '' : 'The deductible is invalid, please replace it with a valid value.'
                            }
                            displayDeselectOption={false}
                        />
                    )}
                </ResponsiveFieldRow>
            </Box>
            <Box marginTop="1rem">
                {intervalOptions[0] && (
                    <SelectInput
                        data-testid="edit-product-interval"
                        label="Interval"
                        name="productServiceInterval"
                        onChange={handleServiceIntervalChange}
                        placeholder={productProps.productServiceInterval}
                        value={
                            !isNil(productProps.productServiceInterval)
                                ? productProps.productServiceInterval
                                : intervalOptions[0].label
                        }
                        hasError={hasProductIntervalError}
                        errorMessage={
                            hasApplicableServiceInterval ? '' : 'The interval is invalid, please replace it with a valid value.'
                        }
                        options={intervalOptions}
                        displayDeselectOption={false}
                    />
                )}
            </Box>
        </>
    );
};

export default EditProductTerms;
